import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, View, Text, Pressable, ScrollView, StyleSheet, Image } from "react-native";
import { MotiView } from "moti";
import { LinearGradient } from "expo-linear-gradient";
import { X, Play, Lock, BookOpen, ChatsTeardrop, GraduationCap } from "@phosphor-icons/react";
import AxioButton from "../../axio-ds/Button/AxioButton";
import { colors, semanticColors, spacing, radii, shadowStyles, typographyScale } from "../../../styles/design-system";
import { getModalAnimationType } from "../../../utils/getModalAnimationType";
import onboardingVideo from "../../../assets/onboarding_video.mp4";

type VideoElementWithFullscreen = HTMLVideoElement & {
  webkitEnterFullscreen?: () => Promise<void> | void;
  webkitRequestFullscreen?: () => Promise<void> | void;
  mozRequestFullScreen?: () => Promise<void> | void;
  msRequestFullscreen?: () => Promise<void> | void;
};

const webVideoStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
  zIndex: 0,
};

export interface WelcomeModalProps {
  visible: boolean;
  onDismiss: () => void;
}

export function WelcomeModal({ visible, onDismiss }: WelcomeModalProps) {
  const [isPlayHovered, setIsPlayHovered] = useState(false);
  const [isCloseHovered, setIsCloseHovered] = useState(false);
  const [hasVideoStarted, setHasVideoStarted] = useState(false);
  const videoRef = useRef<VideoElementWithFullscreen | null>(null);
  const isSafari = typeof navigator !== "undefined" && /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const safariVideoSrc = isSafari
    ? typeof onboardingVideo === "string"
      ? onboardingVideo
      : Image.resolveAssetSource(onboardingVideo)?.uri
    : undefined;

  useEffect(() => {
    if (visible) {
      setHasVideoStarted(false);
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
      }
      return;
    }

    if (!visible && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [visible]);

  const handleVideoPlay = useCallback(() => {
    if (!videoRef.current) {
      return;
    }

    const playPromise = videoRef.current.play();
    if (playPromise) {
      void playPromise.catch(() => null);
    }
  }, []);

  return (
    <Modal
      role="dialog"
      aria-labelledby="welcome-modal"
      visible={visible}
      transparent
      animationType={getModalAnimationType("fade")}
      onRequestClose={onDismiss}>
      <Pressable style={styles.overlay} onPress={onDismiss} accessibilityRole="button" accessibilityLabel="overlay">
        <MotiView
          from={{ opacity: 0, scale: 0.95, translateY: 10 }}
          animate={{ opacity: 1, scale: 1, translateY: 0 }}
          exit={{ opacity: 0, scale: 0.95, translateY: 10 }}
          // @ts-expect-error - Moti transition types are not accurate
          transition={{ type: "spring", damping: 20, stiffness: 300 }}
          style={[styles.modalContainer, shadowStyles["2xl"]]}>
          <Pressable style={styles.modalContent} onPress={(e) => e.stopPropagation()}>
            {/* Header */}
            <View style={styles.header}>
              <Pressable
                onPress={onDismiss}
                onHoverIn={() => setIsCloseHovered(true)}
                onHoverOut={() => setIsCloseHovered(false)}
                accessibilityLabel="Close"
                accessibilityRole="button"
                style={[styles.closeButton, { backgroundColor: isCloseHovered ? colors.grey[100] : "transparent" }]}>
                <X size={20} color={isCloseHovered ? colors.grey[600] : colors.grey[400]} />
              </Pressable>
              <Text style={styles.title}>Welcome to AI Companion</Text>
              <Text style={styles.subtitle}>Your personal learning assistant</Text>
            </View>

            {/* Scrollable Content */}
            <ScrollView
              style={styles.scrollContent}
              contentContainerStyle={styles.scrollContentContainer}
              showsVerticalScrollIndicator={false}>
              {/* Video Embed Placeholder */}
              <View style={styles.videoSection}>
                {typeof window !== "undefined" ? (
                  <View style={styles.videoContainer}>
                    <video
                      ref={videoRef}
                      controls
                      playsInline
                      preload="metadata"
                      src={safariVideoSrc}
                      style={{
                        ...webVideoStyle,
                        opacity: hasVideoStarted ? 1 : 0,
                        pointerEvents: hasVideoStarted ? "auto" : "none",
                      }}
                      onPlay={() => setHasVideoStarted(true)}
                      aria-label="Axio onboarding overview video">
                      {!isSafari ? <source src={onboardingVideo} type="video/mp4" /> : null}
                      Your browser does not support the video tag.
                    </video>
                    {!hasVideoStarted && (
                      <Pressable
                        accessibilityLabel="Play onboarding video"
                        accessibilityRole="button"
                        style={styles.videoOverlay}
                        onPress={handleVideoPlay}
                        onHoverIn={() => setIsPlayHovered(true)}
                        onHoverOut={() => setIsPlayHovered(false)}>
                        <LinearGradient
                          colors={[colors.brand[600], colors.brand[800]]}
                          start={{ x: 0, y: 0 }}
                          end={{ x: 1, y: 1 }}
                          style={styles.videoGradient}>
                          <View style={styles.videoContent}>
                            <MotiView
                              animate={{
                                scale: isPlayHovered ? 1.1 : 1,
                              }}
                              // @ts-expect-error - Moti transition types are not accurate
                              transition={{
                                type: "timing",
                                duration: 150,
                              }}
                              style={styles.playButtonContainer}>
                              <Play size={28} color={colors.white} weight="fill" style={styles.playIcon} />
                            </MotiView>
                            <Text style={styles.videoText}>Watch intro (90 sec)</Text>
                          </View>
                        </LinearGradient>
                      </Pressable>
                    )}
                  </View>
                ) : (
                  <Pressable
                    style={[styles.videoContainer, styles.videoPlaceholder]}
                    onHoverIn={() => setIsPlayHovered(true)}
                    onHoverOut={() => setIsPlayHovered(false)}>
                    <LinearGradient
                      colors={[colors.brand[600], colors.brand[800]]}
                      start={{ x: 0, y: 0 }}
                      end={{ x: 1, y: 1 }}
                      style={styles.videoGradient}>
                      <View style={styles.videoContent}>
                        <MotiView
                          animate={{
                            scale: isPlayHovered ? 1.1 : 1,
                          }}
                          // @ts-expect-error - Moti transition types are not accurate
                          transition={{
                            type: "timing",
                            duration: 150,
                          }}
                          style={styles.playButtonContainer}>
                          <Play size={28} color={colors.white} weight="fill" style={styles.playIcon} />
                        </MotiView>
                        <Text style={styles.videoText}>Watch intro (90 sec)</Text>
                      </View>
                    </LinearGradient>
                  </Pressable>
                )}
              </View>

              {/* Value Props */}
              <View style={styles.valuePropsSection}>
                {/* Privacy */}
                <View style={styles.valuePropCard}>
                  <View style={[styles.iconContainer, styles.iconGreen]}>
                    <Lock size={16} color={colors.success[600]} />
                  </View>
                  <View style={styles.valuePropText}>
                    <Text style={styles.valuePropTitle}>Your conversations are private</Text>
                    <Text style={styles.valuePropDescription}>
                      Instructors can't see your chats - ask anything without worry
                    </Text>
                  </View>
                </View>

                {/* Course Knowledge */}
                <View style={styles.valuePropCard}>
                  <View style={[styles.iconContainer, styles.iconBrand]}>
                    <BookOpen size={16} color={colors.brand[600]} />
                  </View>
                  <View style={styles.valuePropText}>
                    <Text style={styles.valuePropTitle}>I already know your courses</Text>
                    <Text style={styles.valuePropDescription}>
                      No need to explain context - I've read your syllabi and assignments
                    </Text>
                  </View>
                </View>

                {/* No Copy-Pasting */}
                <View style={styles.valuePropCard}>
                  <View style={[styles.iconContainer, styles.iconPurple]}>
                    <ChatsTeardrop size={16} color={colors.accent.purple500} />
                  </View>
                  <View style={styles.valuePropText}>
                    <Text style={styles.valuePropTitle}>No more copy-pasting</Text>
                    <Text style={styles.valuePropDescription}>
                      Stop copying assignments into AI tools - everything is already here
                    </Text>
                  </View>
                </View>

                {/* More Than Homework */}
                <View style={styles.valuePropCard}>
                  <View style={[styles.iconContainer, styles.iconOrange]}>
                    <GraduationCap size={16} color="#ea580c" />
                  </View>
                  <View style={styles.valuePropText}>
                    <Text style={styles.valuePropTitle}>More than just homework help</Text>
                    <Text style={styles.valuePropDescription}>
                      Ask about enrollment, financial aid, campus resources, and more
                    </Text>
                  </View>
                </View>
              </View>
            </ScrollView>

            {/* Footer */}
            <View style={styles.footer}>
              <AxioButton label="Get Started" variant="primary" size="lg" onPress={onDismiss} />
            </View>
          </Pressable>
        </MotiView>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    justifyContent: "center",
    alignItems: "center",
    padding: spacing.sm,
  },
  modalContainer: {
    backgroundColor: colors.white,
    borderRadius: radii.lg,
    maxWidth: 512,
    width: "100%",
    maxHeight: "90%",
    overflow: "hidden",
  },
  modalContent: {
    flex: 1,
    userSelect: "text",
    // @ts-expect-error - cursor works on web
    cursor: "default",
  },
  header: {
    position: "relative",
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
    paddingBottom: spacing.sm,
  },
  closeButton: {
    position: "absolute",
    top: spacing.sm,
    right: spacing.sm,
    padding: spacing.xs,
    borderRadius: radii.full,
    zIndex: 1,
  },
  title: {
    fontSize: typographyScale.displaySm.fontSize,
    lineHeight: typographyScale.displaySm.lineHeight,
    fontWeight: "600",
    color: semanticColors.typography.displayPrimary,
    // @ts-expect-error - cursor works on web
    cursor: "text",
  },
  subtitle: {
    fontSize: typographyScale.textBase.fontSize,
    lineHeight: typographyScale.textBase.lineHeight,
    color: semanticColors.typography.textPrimary,
    marginTop: spacing.xxs,
    // @ts-expect-error - cursor works on web
    cursor: "text",
  },
  scrollContent: {
    flex: 1,
  },
  scrollContentContainer: {
    paddingBottom: spacing.sm,
  },
  videoSection: {
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.sm,
  },
  videoContainer: {
    aspectRatio: 16 / 9,
    borderRadius: radii.xl,
    overflow: "hidden",
    position: "relative",
    backgroundColor: colors.grey[900],
  },
  videoPlaceholder: {
    cursor: "pointer",
  },
  videoGradient: {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
  videoOverlay: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: radii.xl,
    overflow: "hidden",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    zIndex: 1,
  },
  videoContent: {
    alignItems: "center",
  },
  playButtonContainer: {
    width: spacing["4xl"],
    height: spacing["4xl"],
    borderRadius: radii.full,
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: spacing.xs,
  },
  playIcon: {
    marginLeft: 2,
  },
  videoText: {
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: "500",
    color: "rgba(255, 255, 255, 0.9)",
  },
  fullscreenButton: {
    position: "absolute",
    top: spacing.xs,
    right: spacing.xs,
    backgroundColor: "rgba(0, 0, 0, 0.65)",
    borderRadius: radii.full,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xxs,
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xxs,
  },
  fullscreenButtonText: {
    color: colors.white,
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    fontWeight: "500",
  },
  valuePropsSection: {
    paddingHorizontal: spacing.lg,
    gap: spacing.xs,
    borderBottomLeftRadius: radii.xl,
    borderBottomRightRadius: radii.xl,
  },
  valuePropCard: {
    flexDirection: "row",
    alignItems: "flex-start",
    padding: 12,
    borderRadius: radii.xl,
    backgroundColor: colors.grey[50],
    gap: 12,
  },
  iconContainer: {
    width: spacing.xl,
    height: spacing.xl,
    borderRadius: radii.full,
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
  },
  iconGreen: {
    backgroundColor: colors.success[100],
  },
  iconBrand: {
    backgroundColor: colors.brand[100],
  },
  iconPurple: {
    backgroundColor: colors.accent.purple100,
  },
  iconOrange: {
    backgroundColor: colors.warning[100],
  },
  valuePropText: {
    flex: 1,
  },
  valuePropTitle: {
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: "500",
    color: semanticColors.typography.displayPrimary,
    // @ts-expect-error - cursor works on web
    cursor: "text",
  },
  valuePropDescription: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    color: semanticColors.typography.textPrimary,
    marginTop: 2,
    // @ts-expect-error - cursor works on web
    cursor: "text",
  },
  footer: {
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.lg,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.grey[100],
  },
});

export default WelcomeModal;
